import { Injectable, Type } from '@angular/core'
import { SafeResourceUrl } from '@angular/platform-browser'
import { UserAvailabilityListComponent } from '@app/routes/user/availability/user-availability.component'
import { ProfileContainerComponent } from '@app/routes/user/profile-container/profile-container.component'
import { BehaviorSubject, Subject } from 'rxjs'

export interface ContentStackItem<T = any> {
    component: Type<any> // This holds a reference to the Angular component
    context?: T // This holds the context to pass to the component's inputs
    popIfCovered: boolean
    preserveStack: boolean // If current item has popIfCovered set, this overrides it
    showRefreshButton: boolean
    sendToMainPanelUrl?: SafeResourceUrl
}

export interface ContentStackItemOptions {
    popIfCovered: boolean
    preserveStack: boolean // If current item has popIfCovered set, this overrides it
    showRefreshButton?: boolean
    sendToMainPanelUrl?: SafeResourceUrl
}

@Injectable({
    providedIn: 'root',
})
export class ContentStackService {
    constructor() {}
    private contentStack: ContentStackItem[] = []
    private currentContentSubject = new BehaviorSubject<ContentStackItem | null>(null)
    private currentContentStackSizeSubject = new BehaviorSubject<number>(0)
    private refreshSubject = new Subject<void>()

    public currentContent$ = this.currentContentSubject.asObservable()
    public currentConstentStackSize$ = this.currentContentStackSizeSubject.asObservable()
    public refresh$ = this.refreshSubject.asObservable()

    private panelToggledSubject = new Subject<void>()
    public panelToggled$ = this.panelToggledSubject.asObservable()

    pushProfileToDynamicPanel(profileSlug: string, options: ContentStackItemOptions) {
        const stackItem: ContentStackItem = {
            component: ProfileContainerComponent,
            context: { profileSlug: profileSlug, dynamicPanel: true },
            popIfCovered: options.popIfCovered,
            preserveStack: options.preserveStack,
            showRefreshButton: options?.showRefreshButton ?? false,
            sendToMainPanelUrl: 'user/' + profileSlug,
        }
        this.pushContent(stackItem)
    }

    pushUserAvailabilityToDynamicPanel(profileSlug: string, options: ContentStackItemOptions) {
        const stackItem: ContentStackItem = {
            component: UserAvailabilityListComponent,
            context: { profileSlug: profileSlug, dynamicPanel: true },
            popIfCovered: options.popIfCovered,
            preserveStack: options.preserveStack,
            showRefreshButton: options?.showRefreshButton ?? false,
            sendToMainPanelUrl: 'user/' + profileSlug + '/availability',
        }
        this.pushContent(stackItem)
    }

    // Push a new component or content into the stack
    pushContent(content: ContentStackItem): void {
        const currentContent = this.contentStack[this.contentStack.length - 1] || null

        if (currentContent && currentContent.popIfCovered && !content.preserveStack) {
            this.popContent()
        }
        this.contentStack.push(content)
        this.currentContentSubject.next(content)
        this.currentContentStackSizeSubject.next(this.contentStack.length)
    }

    // Pop content when navigating back
    popContent(): void {
        this.contentStack.pop()
        const lastContent = this.contentStack[this.contentStack.length - 1]
        this.currentContentSubject.next(lastContent || null)
        this.currentContentStackSizeSubject.next(this.contentStack.length)
    }

    // Clear the stack (optional)
    clearContent(): void {
        this.contentStack = []
        this.currentContentSubject.next(null)
        this.currentContentStackSizeSubject.next(0)
    }

    // Check if there is content to go back to
    canGoBack(): boolean {
        return this.contentStack.length > 1
    }

    refreshSelected(): void {
        this.refreshSubject.next()
    }

    panelToggled(): void {
        this.panelToggledSubject.next()
    }
}
